// Material Dashboard 2 React - Tables Page with Filters

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Button, TextField } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptIcon from "@mui/icons-material/Receipt";
import UploadIcon from "@mui/icons-material/Upload";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";

// MUI Date Picker
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// React
import { useEffect, useState } from "react";
import Reports from "layouts/report";
import { ExcelUpload } from "layouts/uploadExcel/UploadExcel";

// All Clio / DynamoDB data lives above the routes so it survives navigation.
import { useReportData } from "store/ReportDataContext";

function Tables() {
  const {
    issuedAfter,
    setIssuedAfter,
    issuedBefore,
    setIssuedBefore,
    handleSearch,
    ensureLoaded,
    columns,
    rows,
    mattersColumns,
    mattersRows,
    activitiesColumns,
    activitiesRows,
    persistedMattersColumns,
    persistedMattersRows,
    persistedPaymentsColumns,
    persistedPaymentsRows,
    persistedExpensesColumns,
    persistedExpensesRows,
    billedBills,
    mattersOriginData,
    activitiesOriginData,
    activityKeyedData,
    activityAttorneyMatter,
    combinedInvalidActivities,
    pKeyeddata,
    pdata,
    loadingBills,
    loadingMatters,
    loadingActivities,
    loadingpersistedMatters,
    loadingpersistedPayments,
    loadingpersistedExpenses,
  } = useReportData();

  // Page-local UI state only — the data itself belongs to the shared store.
  const [openReport, setOpenReport] = useState(false);
  const [OpenModalUpload, setOpenModalUpload] = useState(false);
  const [customAdjRefreshKey, setCustomAdjRefreshKey] = useState(0);

  // No-op once the data has been loaded for this page load; a browser reload
  // resets the store and reloads everything.
  useEffect(() => {
    ensureLoaded();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {/* Authors Table with Filters */}
          {OpenModalUpload && (
            <ExcelUpload
              onClose={() => setOpenModalUpload(false)}
              onUploadComplete={(uploadedFileType) => {
                // Bump the key so Reports re-reads the recalculated custom adjustments
                // instead of the ones it fetched when it first mounted.
                if (uploadedFileType === "customAdjustments") {
                  setCustomAdjRefreshKey((prev) => prev + 1);
                }
              }}
            />
          )}
          <Grid item xs={12}>
            <Card>
              <MDBox px={10} pt={2} pb={5}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Issued After"
                        value={issuedAfter}
                        onChange={(newValue) => setIssuedAfter(newValue)}
                        renderInput={(params) => <TextField fullWidth {...params} />}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <DatePicker
                        label="Issued Before"
                        value={issuedBefore}
                        onChange={(newValue) => setIssuedBefore(newValue)}
                        renderInput={(params) => <TextField fullWidth {...params} />}
                      />
                    </Grid>

                    {/* Grouped Buttons Side by Side */}

                    <Grid item xs={12} md={4}>
                      <Grid container spacing={2}>
                        {/* Search Button */}
                        <Grid item xs={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={handleSearch}
                            startIcon={<SearchIcon />}
                            sx={{
                              backgroundColor: "#1976d2",
                              color: "#fff",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            }}
                          >
                            Search
                          </Button>
                        </Grid>

                        {/* Reports Button */}
                        <Grid item xs={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setOpenReport(true)}
                            disabled={
                              loadingActivities ||
                              loadingBills ||
                              loadingMatters ||
                              loadingpersistedExpenses ||
                              loadingpersistedMatters ||
                              loadingpersistedPayments
                            }
                            startIcon={<ReceiptIcon />}
                            sx={{
                              backgroundColor: "#3f6891ff",
                              color: "#fff",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            }}
                          >
                            Reports
                          </Button>
                        </Grid>

                        {/* Upload Button */}
                        <Grid item xs={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<UploadIcon />}
                            onClick={() => setOpenModalUpload(true)} // replace with your handler
                            sx={{
                              backgroundColor: "#388e3c", // green tone
                              color: "#fff",
                              "&:hover": {
                                backgroundColor: "#2e7d32",
                              },
                            }}
                          >
                            Upload
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>
              {openReport && (
                <Reports
                  rows={billedBills}
                  mattersRows={mattersOriginData}
                  activitiesRows={activitiesOriginData}
                  pKeyeddata={pKeyeddata}
                  activityKeyedData={activityKeyedData}
                  activityAttorneyMatter={activityAttorneyMatter}
                  combinedInvalidActivities={combinedInvalidActivities}
                  pdata={pdata}
                  customAdjRefreshKey={customAdjRefreshKey}
                />
              )}
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Bills Table
                </MDTypography>
              </MDBox>

              {/* Filters */}

              {/* Table */}
              {issuedBefore && issuedAfter && !loadingBills ? (
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns, rows: rows }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              ) : (
                <MDBox
                  pt={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px" // optional: gives vertical space
                >
                  <p>loading...</p>
                </MDBox>
              )}
            </Card>
          </Grid>

          {/* Matters Table */}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Matters Table
                </MDTypography>
              </MDBox>
              {issuedBefore && issuedAfter && !loadingMatters ? (
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns: mattersColumns, rows: mattersRows }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              ) : (
                <MDBox
                  pt={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px" // optional: gives vertical space
                >
                  <p>loading...</p>
                </MDBox>
              )}
            </Card>
          </Grid>
          {/* Activities Table*/}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Activities Table
                </MDTypography>
              </MDBox>
              {issuedBefore && issuedAfter && !loadingActivities ? (
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns: activitiesColumns, rows: activitiesRows }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              ) : (
                <MDBox
                  pt={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px" // optional: gives vertical space
                >
                  <p>loading...</p>
                </MDBox>
              )}
            </Card>
          </Grid>
          {/* Persisted Matters Table*/}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Persisted Matters
                </MDTypography>
              </MDBox>
              {!loadingpersistedMatters ? (
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns: persistedMattersColumns, rows: persistedMattersRows }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              ) : (
                <MDBox
                  pt={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px" // optional: gives vertical space
                >
                  <p>loading...</p>
                </MDBox>
              )}
            </Card>
          </Grid>
          {/* Persisted Payments Table*/}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Persisted Payments
                </MDTypography>
              </MDBox>
              {!loadingpersistedPayments ? (
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns: persistedPaymentsColumns, rows: persistedPaymentsRows }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              ) : (
                <MDBox
                  pt={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px" // optional: gives vertical space
                >
                  <p>loading...</p>
                </MDBox>
              )}
            </Card>
          </Grid>
          {/* Persisted Expenses Table*/}
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Persisted Expenses
                </MDTypography>
              </MDBox>
              {!loadingpersistedExpenses ? (
                <MDBox pt={3}>
                  <DataTable
                    table={{ columns: persistedExpensesColumns, rows: persistedExpensesRows }}
                    isSorted={true}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>
              ) : (
                <MDBox
                  pt={3}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px" // optional: gives vertical space
                >
                  <p>loading...</p>
                </MDBox>
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default Tables;
